import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@mui/icons-material";
import SubCategoryItem from "src/components/core-components/mui/SubCategoryItem";
import ChildCategoryItem from "src/components/core-components/mui/ChildCategoryItem";
import { useTranslate } from "src/locales";

interface RequestSidebarAreaProps {
  title: string;
  upn: string;
  protocolId: number | null;
  categories: any[];
  selectedMainCategory: string;
  selectedSubCategory: string;
  expandedCategories: string[];
  sidebarSearchText: string;
  handleMainCategorySelect: (id: string) => void;
  handleSubCategorySelect: (id: string) => void;
  handleCategoryToggle: (id: string) => void;
  handleSidebarSearch: (text: string) => void;
}

const RequestSidebarArea: React.FC<RequestSidebarAreaProps> = ({
  title,
  upn,
  protocolId,
  categories,
  selectedMainCategory,
  selectedSubCategory,
  expandedCategories,
  sidebarSearchText,
  handleMainCategorySelect,
  handleSubCategorySelect,
  handleCategoryToggle,
  handleSidebarSearch,
}) => {
  const { t } = useTranslate();

  // Derived lists based on 'type' property added in the hook
  const mainCategories = categories.filter((c) => c.type === "main");
  const paginatedSubCategories = categories.filter((c) => c.type === "sub"); // Using paginated var name to match original flow despite not paginating
  const childCategories = categories.filter((c) => c.type === "child");

  return (
    <Box sx={{ width: 300, borderRight: 1, borderColor: "divider", p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
        {t("patient-request-service.request-title")}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem", mr: 0.8 }}
          >
            {t("patient-request-service.patient-info.name-surname")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem", mr: 0.8 }}
          >
            {t("patient-request-service.patient-info.file-protocol")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
          >
            {upn} {protocolId ? ` / ${protocolId}` : ""}
          </Typography>
        </Box>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder={t("patient-request-service.search.placeholder")}
        value={sidebarSearchText}
        onChange={(e) => handleSidebarSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ backgroundColor: "#f6f6f6", borderRadius: 1, p: 1 }}>
        <List sx={{ p: 0 }}>
          {mainCategories.map((category) => (
            <ListItem key={category.id} sx={{ p: 0, mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMainCategorySelect(category.id)}
                sx={{
                  borderRadius: 1,
                  backgroundColor: "transparent",
                  color:
                    selectedMainCategory === category.id
                      ? "primary.main"
                      : "inherit",
                  fontWeight:
                    selectedMainCategory === category.id ? "bold" : "normal",
                  pl: 1,
                  pr: 1,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: 24 }}>
                  <KeyboardArrowRightIcon
                    sx={{ color: "inherit", fontSize: 20 }}
                  />
                </ListItemIcon>
                <ListItemText primary={category.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2, borderColor: "#d0d0d0", borderWidth: 2 }} />

      <List sx={{ p: 0 }}>
        {paginatedSubCategories.map((category) => {
          const isParentExpanded = expandedCategories.includes(
            category.parent || ""
          );
          const isCategoryExpanded = expandedCategories.includes(category.id);
          const categoryChilds = childCategories.filter(
            (child) => child.parent === category.id
          );
          const isVisible =
            selectedMainCategory === "tümü" ||
            selectedMainCategory === "sık-kullanılanlar" ||
            isParentExpanded;
          const isSelected =
            (selectedSubCategory === category.id &&
              // We check view in parent, here just relying on ID match
              true) ||
            (isCategoryExpanded && selectedMainCategory === "tümü");

          return (
            <div key={category.id}>
              <SubCategoryItem
                category={category}
                isSelected={isSelected}
                isExpanded={isCategoryExpanded}
                isVisible={isVisible}
                selectedMainCategory={selectedMainCategory}
                onClick={() => handleSubCategorySelect(category.id)}
                onToggle={() => handleCategoryToggle(category.id)}
              />

              {categoryChilds.map((childCategory) => (
                <ChildCategoryItem
                  key={childCategory.id}
                  category={childCategory}
                  isSelected={selectedSubCategory === childCategory.id}
                  isVisible={isCategoryExpanded}
                  onClick={() => handleSubCategorySelect(childCategory.id)}
                />
              ))}
            </div>
          );
        })}
      </List>
    </Box>
  );
};

export default RequestSidebarArea;
